import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Moon, Sun, Menu, X, PenLine, User } from "lucide-react";
import { useAuth } from "../auth";
import { fetchNotifications } from "../api";

interface HeaderProps {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const navLinks = [
  { label: "首页", href: "/" },
  { label: "文章", href: "/articles" },
  { label: "API 文档", href: "/api-docs" },
];

export function Header({ theme, toggleTheme }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const load = () => void fetchNotifications(1).then(data => setUnreadCount(data.unreadCount)).catch(() => undefined);
    load(); const timer = window.setInterval(load, 60000); window.addEventListener("notifications-updated", load);
    return () => { window.clearInterval(timer); window.removeEventListener("notifications-updated", load); };
  }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node))
        setAccountOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(
    () => () => {
      if (accountCloseTimer.current) clearTimeout(accountCloseTimer.current);
    },
    [],
  );
  const openAccount = () => {
    if (accountCloseTimer.current) clearTimeout(accountCloseTimer.current);
    setAccountOpen(true);
  };
  const closeAccountLater = () => {
    accountCloseTimer.current = setTimeout(() => setAccountOpen(false), 180);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-[var(--bg-glass)] backdrop-blur-xl shadow-lg border-[var(--border-default)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="w-full px-3 sm:px-5 flex items-center justify-between h-16 gap-4">
        {/* Logo */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 shrink-0 group"
        >
          <img src="/ds-icon.png" alt="DevSpec" className="w-9 h-9 rounded-xl object-cover shadow-lg group-hover:shadow-[var(--shadow-glow)] transition-shadow" />
          <span className="text-xl font-black tracking-tight gradient-brand">
            DevSpec
          </span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => navigate(link.href)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(link.href)
                  ? "text-primary-light bg-primary/10"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden sm:block flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/editor")}
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-primary to-accent shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <PenLine className="w-4 h-4" />
            写文章
          </button>

          {user ? (
            <><button onClick={() => navigate("/notifications")} className="relative w-9 h-9 rounded-full hidden sm:inline-flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]" aria-label={`通知${unreadCount ? `，${unreadCount} 条未读` : ""}`}><Bell className="w-[18px] h-[18px]" />{unreadCount > 0 && <span className="absolute -right-1 -top-1 min-w-5 h-5 px-1 rounded-full bg-[#e53935] text-white text-[10px] flex items-center justify-center">{unreadCount > 99 ? "99+" : unreadCount}</span>}</button><div
              ref={accountRef}
              className="relative hidden sm:block"
              onMouseEnter={openAccount}
              onMouseLeave={closeAccountLater}
            >
              <button
                onClick={() => setAccountOpen(!accountOpen)}
                onFocus={openAccount}
                className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
                aria-label="个人账户"
                aria-expanded={accountOpen}
                aria-haspopup="menu"
              >
                <User className="w-[18px] h-[18px]" />
              </button>
              {accountOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full w-60 p-2 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-lg z-50"
                >
                  <div className="px-3 py-3 mb-1 border-b border-[var(--border-subtle)]">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-sm truncate">
                        {user.username}
                      </p>
                      <span className="shrink-0 px-2 py-0.5 rounded-full border border-[var(--border-default)] text-xs">
                        {user.role === "admin" ? "管理员" : "普通用户"}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-tertiary)] truncate mt-1">
                      {user.email}
                    </p>
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => {
                      navigate(`/users/${user.id}`);
                      setAccountOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded text-sm hover:bg-[var(--bg-hover)]"
                  >
                    我的主页
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      navigate("/settings");
                      setAccountOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded text-sm hover:bg-[var(--bg-hover)]"
                  >
                    个人设置
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      navigate("/settings?tab=api");
                      setAccountOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded text-sm hover:bg-[var(--bg-hover)]"
                  >
                    我的 API
                  </button>
                  {user.role === "admin" && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        navigate("/settings?tab=categories");
                        setAccountOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 rounded text-sm hover:bg-[var(--bg-hover)]"
                    >
                      文章分类设置
                    </button>
                  )}
                  <button
                    role="menuitem"
                    onClick={toggleTheme}
                    className="w-full px-3 py-2 rounded text-sm hover:bg-[var(--bg-hover)] flex items-center justify-between"
                  >
                    <span>主题</span>
                    <span className="inline-flex items-center gap-2 text-[var(--text-secondary)]">
                      {theme === "light" ? "深色" : "浅色"}
                      {theme === "light" ? (
                        <Moon className="w-4 h-4" />
                      ) : (
                        <Sun className="w-4 h-4" />
                      )}
                    </span>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      logout();
                      setAccountOpen(false);
                      navigate("/");
                    }}
                    className="w-full text-left px-3 py-2 mt-1 border-t border-[var(--border-subtle)] text-sm text-[#d32f2f] dark:text-[#ff6b6b] hover:bg-red-500/10"
                  >
                    退出登录
                  </button>
                </div>
              )}
            </div></>
          ) : (
            <div className="hidden sm:flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
                aria-label="切换主题"
              >
                {theme === "light" ? (
                  <Moon className="w-4 h-4" />
                ) : (
                  <Sun className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => navigate("/login")}
                className="h-9 px-3 text-sm text-[var(--text-secondary)] hover:text-primary"
              >
                登录
              </button>
            </div>
          )}

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="sm:hidden w-9 h-9 rounded-lg flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
          >
            {mobileOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="sm:hidden border-t border-[var(--border-default)] bg-[var(--bg-surface)]/95 backdrop-blur-xl">
          <div className="w-full px-3 sm:px-5 py-4 space-y-3">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => {
                    navigate(link.href);
                    setMobileOpen(false);
                  }}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-medium ${
                    isActive(link.href)
                      ? "text-primary-light bg-primary/10"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => {
                  navigate("/editor");
                  setMobileOpen(false);
                }}
                className={`text-left px-3 py-2 rounded-lg text-sm ${user ? "text-[#d32f2f] dark:text-[#ff6b6b]" : "text-[var(--text-secondary)]"}`}
              >
                写文章
              </button>
              {user && <button onClick={() => { navigate("/notifications"); setMobileOpen(false); }} className="text-left px-3 py-2 rounded-lg text-sm text-[var(--text-secondary)]">通知消息{unreadCount ? `（${unreadCount} 条未读）` : ""}</button>}
              <button
                onClick={() => {
                  if (user) logout();
                  else navigate("/login");
                  setMobileOpen(false);
                }}
                className="text-left px-3 py-2 rounded-lg text-sm text-[var(--text-secondary)]"
              >
                {user
                  ? `${user.username}（${user.role === "admin" ? "管理员" : "普通用户"}）· 退出`
                  : "登录 / 注册"}
              </button>
              <button
                onClick={toggleTheme}
                className="text-left px-3 py-2 rounded-lg text-sm text-[var(--text-secondary)]"
              >
                主题：{theme === "light" ? "切换为深色" : "切换为浅色"}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
